import { useEffect, useMemo, useRef, useState } from "react";

export interface LogLine {
  /** Optional sequence id from the server — used to dedupe across SSE batches. */
  seq?: number;
  ts: string;
  level: "info" | "warn" | "error" | string;
  source: string;
  line: string;
}

interface LogPanelProps {
  title: string;
  lines: LogLine[];
  /** Show a "Live" badge to indicate SSE connection. */
  live?: boolean;
  /** Show levels filter chips. Default true. */
  filterable?: boolean;
  /** Empty-state copy. */
  emptyHint?: string;
  /** Max visible lines (UI windowing — no virtualisation; keep <= ~1000). */
  maxLines?: number;
  /** Override panel height. */
  height?: number | string;
}

const LEVEL_COLOR: Record<string, string> = {
  info: "var(--color-text-secondary)",
  warn: "var(--color-warning)",
  error: "var(--color-danger)"
};

/**
 * Mono-spaced log viewer with level filters and auto-scroll-to-bottom.
 * Used on HostDetail (live agent logs) and DeviceDetail (install transcript).
 */
export function LogPanel({
  title,
  lines,
  live = false,
  filterable = true,
  emptyHint = "No log entries yet.",
  maxLines = 1000,
  height = 320
}: LogPanelProps) {
  const [enabledLevels, setEnabledLevels] = useState<Record<string, boolean>>({
    info: true,
    warn: true,
    error: true
  });
  const [autoscroll, setAutoscroll] = useState(true);
  const [query, setQuery] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);

  const filtered = useMemo(() => {
    let xs = lines;
    if (filterable) xs = xs.filter((l) => enabledLevels[l.level] !== false);
    if (query.trim())
      xs = xs.filter((l) => l.line.toLowerCase().includes(query.trim().toLowerCase()));
    if (xs.length > maxLines) xs = xs.slice(-maxLines);
    return xs;
  }, [lines, enabledLevels, query, filterable, maxLines]);

  // Auto-scroll-to-bottom when new lines arrive, unless the user is reading
  // history (scrolled up away from bottom).
  useEffect(() => {
    if (!autoscroll || !scrollRef.current) return;
    if (userScrolledUp.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [filtered.length, autoscroll]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    userScrolledUp.current = !atBottom;
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = { info: 0, warn: 0, error: 0 };
    for (const ln of lines) c[ln.level] = (c[ln.level] ?? 0) + 1;
    return c;
  }, [lines]);

  return (
    <div
      style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        overflow: "hidden"
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-md)",
          padding: "var(--space-md) var(--space-lg)",
          borderBottom: "var(--border-default)",
          flexWrap: "wrap"
        }}
      >
        <h2
          style={{
            fontSize: "var(--font-size-lg)",
            fontWeight: 600,
            color: "var(--color-primary)",
            margin: 0
          }}
        >
          {title}
        </h2>
        {live && (
          <span
            title="Live updates via SSE"
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: "var(--radius-pill)",
              background: "var(--color-status-available-bg, rgba(0,200,0,.15))",
              color: "var(--color-status-available-text, #1a8a1a)"
            }}
          >
            ● LIVE
          </span>
        )}
        <span
          style={{
            fontSize: "var(--font-size-xs)",
            color: "var(--color-text-secondary)"
          }}
        >
          {filtered.length} of {lines.length} lines
        </span>
        <div style={{ flex: 1 }} />
        {filterable && (
          <div style={{ display: "flex", gap: 6 }}>
            {(["info", "warn", "error"] as const).map((lvl) => (
              <button
                key={lvl}
                onClick={() =>
                  setEnabledLevels((prev) => ({ ...prev, [lvl]: !prev[lvl] }))
                }
                style={{
                  padding: "2px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  border: "1px solid",
                  borderColor: enabledLevels[lvl]
                    ? LEVEL_COLOR[lvl]
                    : "var(--color-text-secondary)",
                  borderRadius: "var(--radius-pill)",
                  background: enabledLevels[lvl] ? LEVEL_COLOR[lvl] : "transparent",
                  color: enabledLevels[lvl]
                    ? "#fff"
                    : "var(--color-text-secondary)",
                  cursor: "pointer",
                  opacity: enabledLevels[lvl] ? 1 : 0.6
                }}
              >
                {lvl} ({counts[lvl] ?? 0})
              </button>
            ))}
          </div>
        )}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter…"
          style={{
            padding: "4px 8px",
            fontSize: "var(--font-size-xs)",
            border: "var(--border-default)",
            borderRadius: "var(--radius-sm)",
            background: "var(--color-foreground)",
            outline: "none",
            width: 180
          }}
        />
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            color: "var(--color-text-secondary)",
            cursor: "pointer"
          }}
        >
          <input
            type="checkbox"
            checked={autoscroll}
            onChange={(e) => {
              setAutoscroll(e.target.checked);
              if (e.target.checked) userScrolledUp.current = false;
            }}
          />
          autoscroll
        </label>
      </div>

      {/* Body */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          height,
          overflow: "auto",
          background: "#0e141b",
          color: "#d1d9e1",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          lineHeight: 1.5,
          padding: "var(--space-sm) var(--space-md)"
        }}
      >
        {filtered.length === 0 ? (
          <div style={{ color: "#6c7787", padding: "var(--space-md) 0" }}>
            {emptyHint}
          </div>
        ) : (
          filtered.map((ln, i) => (
            <div
              key={ln.seq ?? `${ln.ts}-${i}`}
              style={{ display: "flex", gap: 8, whiteSpace: "pre-wrap" }}
            >
              <span style={{ color: "#6c7787", flexShrink: 0 }}>
                {formatTs(ln.ts)}
              </span>
              <span
                style={{
                  color: LEVEL_COLOR[ln.level] ?? "#9aa6b3",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  width: 44,
                  flexShrink: 0
                }}
              >
                {ln.level}
              </span>
              <span
                style={{
                  color: "#7892b8",
                  width: 160,
                  flexShrink: 0,
                  // Prevent the source text from bleeding into the message
                  // column when the kind is long (e.g. "execution.finished").
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
                title={ln.source}
              >
                {ln.source}
              </span>
              <span style={{ flex: 1, wordBreak: "break-word" }}>{ln.line}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function formatTs(iso: string): string {
  // HH:MM:SS.sss for compactness; full date is in title attr if needed
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 19);
  return (
    d.toTimeString().slice(0, 8) +
    "." +
    String(d.getMilliseconds()).padStart(3, "0")
  );
}
